import { useState, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { useGetEmailQueueBlacklist } from 'hooks/queue/useEmailQueueBlacklist';
import './EmailAutocomplete.scss';
import { useParams } from 'react-router-dom';

interface EmailAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxEmails?: number;
}

export const EmailAutocomplete = ({ value, onChange, placeholder, maxEmails }: EmailAutocompleteProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { locationId } = useParams();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useGetEmailQueueBlacklist(locationId);

  const queueEmailLists = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'emails' in data && Array.isArray(data.emails)) {
      return data.emails;
    }
    return [];
  }, [data]);

  const prefix = prefixClass('email-autocomplete');

  // Calculate selected emails and current email being typed
  const selectedEmails = value
    .split(',')
    .map(e => e.trim())
    .filter(e => e.length > 0);
  const currentEmail = inputValue.trim();
  const emailCount = selectedEmails.length;
  const hasReachedLimit = maxEmails !== undefined && emailCount >= maxEmails;

  const filteredOptions = currentEmail
    ? queueEmailLists.filter((option: string) => option.toLowerCase().includes(currentEmail.toLowerCase()))
    : queueEmailLists;

  // Sync inputValue when parent resets the value (e.g., modal close, form reset)
  useEffect(() => {
    if (value === '') {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Only open if user is typing something
    const current = newValue.trim();
    const reachedLimit = hasReachedLimit;

    if (current.length > 0 && !isOpen && !reachedLimit) {
      setIsOpen(true);
    } else if (current.length === 0 || reachedLimit) {
      setIsOpen(false);
    }
  };

  const handleSelectEmail = (email: string) => {
    // Check if we've reached the limit before adding
    if (maxEmails !== undefined && selectedEmails.length >= maxEmails) {
      setIsOpen(false);
      return;
    }

    // Add the selected email to the list
    const newEmails = [...selectedEmails, email];
    onChange(newEmails.join(', '));
    setInputValue('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const commitCurrentInput = () => {
    const email = inputValue.trim();
    if (!email) return;
    if (maxEmails !== undefined && selectedEmails.length >= maxEmails) return;
    const newEmails = [...selectedEmails, email];
    onChange(newEmails.join(', '));
    setInputValue('');
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    const newEmails = selectedEmails.filter(email => email !== emailToRemove);
    onChange(newEmails.join(', '));
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to remove last chip if input is empty
    if (e.key === 'Backspace' && inputValue === '' && selectedEmails.length > 0) {
      e.preventDefault();
      handleRemoveEmail(selectedEmails[selectedEmails.length - 1]);
      return;
    }

    // Commit on comma or Tab
    if (e.key === ',' || e.key === 'Tab') {
      if (inputValue.trim()) {
        e.preventDefault();
        commitCurrentInput();
        return;
      }
    }

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      // Open dropdown on arrow keys if there are options
      if (queueEmailLists.length > 0 && !hasReachedLimit) {
        setIsOpen(true);
      }
      return;
    }

    if (!isOpen) {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        commitCurrentInput();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
          handleSelectEmail(filteredOptions[selectedIndex]);
        } else {
          commitCurrentInput();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    // Open dropdown on focus if not at limit and there are options
    if (!hasReachedLimit && queueEmailLists.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    commitCurrentInput();
  };

  const highlightMatch = (text: string, search: string) => {
    if (!search) return text;

    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <span key={index} className={prefix('__highlight')}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className={prefix()} ref={containerRef}>
      <div className={prefix('__input-wrapper')}>
        {selectedEmails.map((email, index) => (
          <div key={index} className={prefix('__chip')}>
            <span className={prefix('__chip-text')}>{email}</span>
            <button
              type="button"
              className={prefix('__chip-remove')}
              onClick={() => handleRemoveEmail(email)}
              aria-label="Remove email"
            >
              ×
            </button>
          </div>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={selectedEmails.length === 0 ? placeholder : ''}
          className={prefix('__input')}
          disabled={hasReachedLimit}
        />
      </div>

      {isOpen && filteredOptions.length > 0 && !hasReachedLimit && (
        <div className={prefix('__dropdown')}>
          {filteredOptions.map((option: string, index: number) => (
            <div
              key={option}
              className={classNames(prefix('__option'), {
                [prefix('__option--selected')]: index === selectedIndex,
              })}
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSelectEmail(option)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className={prefix('__option-email')}>{highlightMatch(option, currentEmail)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
